import React, { useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { KpiIcon } from "../../../../DataManagment/Quantative/component/KpiIcons";
import NodataFound from "../../../../../img/no.png";
import InvitationStatusBadge from "./InvitationStatusBadge";
import RowActionMenu from "./RowActionMenu";
import {
  cardSurface,
  filterBarStyle,
  tableCardSurface,
  thStyle,
} from "./supplierManagementStyles";
import {
  copySupplierInvitationLink,
  formatDateTime,
  getInvitationActionMenuItems,
  invitationSearchText,
  isInvitationExpired,
} from "./supplierManagementHelpers";

const InvitationsList = ({
  rows,
  loading,
  openMenuId,
  setOpenMenuId,
  onOpenInvitationDetails,
  onOpenResend,
  onInvite,
}) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!rows?.length) return [];
    const q = search.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    return rows.filter((row) => {
      const haystack = invitationSearchText(row);
      if (tokens.length && !tokens.every((t) => haystack.includes(t))) {
        return false;
      }
      return true;
    });
  }, [rows, search]);

  const total = rows?.length ?? 0;
  const filteredCount = filtered.length;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: 48,
          ...cardSurface,
        }}
      >
        <div className="kpi-spinner" />
        <span style={{ fontSize: 13, color: "var(--ink-4)", fontWeight: 600 }}>
          Loading invitations…
        </span>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div
        style={{
          ...cardSurface,
          textAlign: "center",
          padding: "48px 24px",
        }}
      >
        <img
          style={{ opacity: 0.2, maxWidth: 280, width: "100%" }}
          src={NodataFound}
          alt=""
        />
        <p
          style={{
            marginTop: 16,
            color: "var(--ink-3)",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          No pending invitations
        </p>
        <button
          type="button"
          className="kpi-btn-primary"
          style={{ marginTop: 12 }}
          onClick={onInvite}
        >
          + Invite supplier
        </button>
      </div>
    );
  }

  return (
    <>
      <div style={filterBarStyle}>
        <div
          style={{
            position: "relative",
            flex: "1 1 220px",
            minWidth: 180,
            maxWidth: 420,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 11,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ink-4)",
              pointerEvents: "none",
            }}
          >
            <KpiIcon name="search" size={14} />
          </div>
          <input
            className="kpi-input"
            style={{ paddingLeft: 34, paddingRight: search ? 32 : 12 }}
            placeholder="Search company, email, status…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search supplier invitations"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 18,
                color: "var(--ink-4)",
                lineHeight: 1,
                padding: 0,
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>

        {search.trim() ? (
          <button
            type="button"
            className="kpi-btn-secondary"
            onClick={() => setSearch("")}
          >
            Clear filters
          </button>
        ) : null}

        <span
          style={{
            marginLeft: "auto",
            fontSize: 12,
            color: "var(--ink-4)",
            fontFamily: "var(--font-mono)",
            whiteSpace: "nowrap",
          }}
        >
          {search.trim() && filteredCount !== total
            ? `Showing ${filteredCount} of ${total}`
            : `${total} invitation${total === 1 ? "" : "s"}`}
        </span>
      </div>

      {filteredCount === 0 ? (
        <div
          style={{
            ...cardSurface,
            textAlign: "center",
            padding: "40px 24px",
          }}
        >
          <p
            style={{
              color: "var(--ink-3)",
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            No invitations match your search
          </p>
          <button
            type="button"
            className="kpi-btn-secondary"
            onClick={() => setSearch("")}
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="supplier-mgmt-table-wrap" style={tableCardSurface}>
          <div className="supplier-mgmt-table-scroll">
            <Table hover className="mb-0 supplier-mgmt-table">
              <thead>
                <tr>
                  <th className="supplier-mgmt-sr-cell" style={thStyle}>Sr</th>
                  <th style={{ ...thStyle, minWidth: 160 }}>Company</th>
                  <th style={thStyle}>Email</th>
                  <th style={{ ...thStyle, width: 150 }}>Invited</th>
                  <th style={{ ...thStyle, width: 150 }}>Expires</th>
                  <th style={{ ...thStyle, width: 110 }}>Status</th>
                  <th style={{ ...thStyle, width: 56, textAlign: "center" }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => {
                  const expired = isInvitationExpired(row.expiresAt);
                  return (
                    <tr key={row.id ?? row.email ?? index}>
                      <td className="supplier-mgmt-sr-cell">{index + 1}</td>
                      <td>
                        <div style={{ fontWeight: 600, color: "var(--ink-2)" }}>
                          {row.companyName || "—"}
                        </div>
                      </td>
                      <td className="small">{row.email || "—"}</td>
                      <td className="small" style={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(row.createdAt)}
                      </td>
                      <td
                        className="small"
                        style={{
                          whiteSpace: "nowrap",
                          color: expired ? "#b91c1c" : undefined,
                          fontWeight: expired ? 600 : undefined,
                        }}
                      >
                        {formatDateTime(row.expiresAt)}
                        {expired ? (
                          <span
                            className="d-block"
                            style={{
                              fontSize: 10,
                              fontFamily: "var(--font-mono)",
                              textTransform: "uppercase",
                            }}
                          >
                            Expired
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <InvitationStatusBadge statusKey={row.statusKey} />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <RowActionMenu
                          menuId={`invitation-${row.id ?? index}`}
                          openMenuId={openMenuId}
                          setOpenMenuId={setOpenMenuId}
                          items={getInvitationActionMenuItems(row, {
                            openInvitationDetails: onOpenInvitationDetails,
                            openResend: onOpenResend,
                            copyLink: copySupplierInvitationLink,
                          })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </>
  );
};

export default InvitationsList;
